import { memo, ReactNode } from 'react';
import {
  ActionIcon,
  Breadcrumbs,
  Flex,
  Group,
  Text,
  Title,
  Anchor,
} from '@mantine/core';
import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  backUrl?: string;
  actions?: ReactNode;
  showBackButton?: boolean;
}

const PageHeader = memo<PageHeaderProps>(
  ({
    title,
    subtitle,
    breadcrumbs = [],
    backUrl = '/sessions',
    actions,
    showBackButton = true,
  }) => {
    const navigate = useNavigate();

    const handleBackClick = () => {
      if (backUrl) {
        navigate(backUrl);
      }
    };

    return (
      <>
        {/* Header con navigazione */}
        <Group gap={5} align='center'>
          {showBackButton && breadcrumbs.length > 0 && (
            <ActionIcon variant='light' onClick={handleBackClick} size='lg'>
              <ChevronLeft />
            </ActionIcon>
          )}

          {breadcrumbs.length > 0 && (
            <Breadcrumbs>
              {breadcrumbs.map((item, index) =>
                item.onClick || item.href ? (
                  <Anchor
                    key={index}
                    onClick={
                      item.onClick || (() => item.href && navigate(item.href))
                    }
                    size='sm'
                    c='blue'
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'none',
                      },
                    }}
                  >
                    {item.label}
                  </Anchor>
                ) : (
                  <Text key={index} size='sm' c='dimmed'>
                    {item.label}
                  </Text>
                )
              )}
            </Breadcrumbs>
          )}
        </Group>

        {title && (
          <Flex justify='space-between' align='center' gap='md' pt='md' pb='sm'>
            <div>
              <Title order={2}>{title}</Title>
              {subtitle && (
                <Text c='dimmed' size='sm'>
                  {subtitle}
                </Text>
              )}
            </div>
            {actions && <Group gap='sm'>{actions}</Group>}
          </Flex>
        )}
      </>
    );
  }
);

PageHeader.displayName = 'PageHeader';

export default PageHeader;
